import ConfigurationInput from "src/pages/admin-pages/generalSettings/settingsInput";
import { Icon } from "@nubeteck/components";
import { Button, Flex, Form, GetRef, Input, InputNumber, InputRef, Modal, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import { OptionProps } from "antd/es/select";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "src/hooks";
import parse from "html-react-parser";

interface EditableRowProps {
  index: number;
}

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  dataIndex: string;
  record: any;
  defaultValue: number;
  max?: number;
  min?: number;
  onchange: () => void;
  fieldType: "options" | "text" | "number" | "grade-feedback" | "longText" | "richText";
  handleSave: (record: any) => void;
  options?: OptionProps[] | [];
}

// type EditableTableProps = Parameters<typeof Table>[0];

// type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

type FormInstance<T> = GetRef<typeof Form<T>>;

const EditableContext = React.createContext<FormInstance<any> | null>(null);

export const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} key={index} />
      </EditableContext.Provider>
    </Form>
  );
};

export const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  children,
  dataIndex,
  editable,
  record,
  max,
  min,
  handleSave,
  fieldType,
  defaultValue,
  options,
  onchange,
  //   ...restProps
}) => {
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;
  const [modalOpen, setModalOpen] = useState(false);
  const [btnFeedbackVisible, setBtnFeedbackVisible] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState(null);
  const isMobile = useMediaQuery("(max-width: 1180px)");

  useEffect(() => {
    if (record && record[dataIndex] && record[dataIndex] !== "")
      if (fieldType == "grade-feedback") {
        if (record && record[dataIndex] && record[dataIndex] !== "") {
          form.setFieldsValue({
            [`calificacion${dataIndex}`]: record[dataIndex]?.calificacion,
            [`feedback${dataIndex}`]: record[dataIndex]?.feedback,
          });
          setFeedbackValue(record[dataIndex]?.feedback);
        }
      } else {
        if (record && record[dataIndex] && record[dataIndex] !== "") {
          form.setFieldsValue({ [dataIndex]: record[dataIndex] });
        } else if (defaultValue) {
          form.setFieldsValue({ [dataIndex]: defaultValue });
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const save = async () => {
    try {
      const values = await form.validateFields();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  const guardarCalificacionFeedback = () => {
    handleSave({
      ...record,
      [dataIndex]: {
        calificacion: form.getFieldValue(`calificacion${dataIndex}`),
        feedback: form.getFieldValue(`feedback${dataIndex}`),
      },
    });
    setModalOpen(false);
  };

  const childNode = children;
  return (
    <>
      {editable ? (
        <td
          onMouseEnter={() => setBtnFeedbackVisible(true)}
          onMouseLeave={() => setBtnFeedbackVisible(false)}
        >
          {fieldType == "grade-feedback" ? (
            <Flex>
              <Form.Item name={`calificacion${dataIndex}`} style={{ margin: "auto 0px" }}>
                <InputNumber
                  onChange={onchange}
                  style={{ width: 70 }}
                  size="small"
                  min={min ?? 0}
                  max={max ?? 100}
                  controls={false}
                  onPressEnter={guardarCalificacionFeedback}
                  onBlur={guardarCalificacionFeedback}
                />
              </Form.Item>
              <div style={{ width: 16 }}>
                {(btnFeedbackVisible || feedbackValue) && (
                  <Button
                    type="text"
                    icon={<Icon name="" outlined color="#A7A7A7" size={17} />}
                    onClick={() => setModalOpen(true)}
                  />
                )}
              </div>
              <Modal
                open={modalOpen}
                closable={false}
                footer={
                  <Flex gap={10}>
                    <Button type="primary" onClick={() => guardarCalificacionFeedback()}>
                      Aceptar
                    </Button>
                    <Button
                      onClick={() => {
                        setModalOpen(false);
                        form.setFieldValue(`feedback${dataIndex}`, record[dataIndex]?.feedback);
                      }}
                    >
                      Cancelar
                    </Button>
                  </Flex>
                }
              >
                <Form.Item name={`feedback${dataIndex}`} label="Retroalimentación">
                  <Input.TextArea ref={inputRef} />
                </Form.Item>
              </Modal>
            </Flex>
          ) : (
            <Form.Item style={{ margin: 0 }} name={dataIndex}>
              {fieldType == "richText" ? (
                <ConfigurationInput
                  data={{
                    id: 1,
                    label: "",
                    nombre: dataIndex,
                    tipo: "Editor",
                    valor: record[dataIndex],
                    descripcion: "Comentarios",
                  }}
                  onChange={onchange}
                  onBlur={save}
                />
              ) : fieldType == "longText" ? (
                <TextArea ref={inputRef} onPressEnter={save} onBlur={save} onChange={onchange} />
              ) : fieldType == "text" ? (
                <Input ref={inputRef} onPressEnter={save} onBlur={save} onChange={onchange} />
              ) : fieldType == "number" ? (
                <InputNumber
                  size="small"
                  min={min ?? 0}
                  max={max ?? 100}
                  controls={false}
                  onPressEnter={save}
                  onBlur={save}
                  onChange={onchange}
                />
              ) : (
                <Select options={options} onBlur={save} onChange={onchange} />
              )}
            </Form.Item>
          )}
        </td>
      ) : fieldType == "grade-feedback" ? (
        <td title={record[dataIndex]?.feedback}>{record[dataIndex]?.calificacion}</td>
      ) : fieldType == "richText" ? (
        <div>{parse(record[dataIndex] ?? "")}</div>
      ) : (
        <td
          style={{
            position: dataIndex === "estudianteNombre" && !isMobile ? "sticky" : "initial",
            left: 0,
            backgroundColor: "white",
            zIndex: 3,
          }}
        >
          {childNode}
          {dataIndex === "totalProyectado" ? "%" : null}
        </td>
      )}
    </>
  );
};

export const editableComponents = {
  body: {
    row: EditableRow,
    cell: EditableCell,
  },
};
